import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useAnalyticalDatasetDefinitionStore } from '../state/useAnalyticalDatasetDefinitionStore';
import type { SourceCohortDisplay, UseSourceCohortsSectionResult } from '../types';
import { useAnalyticalDatasetEditLock } from './useAnalyticalDatasetEditLock';
import { useInheritedStudyPeriod } from './useInheritedStudyPeriod';
import { useSelectedSourceCohort } from './useSelectedSourceCohort';

export const useSourceCohortsSection = (): UseSourceCohortsSectionResult => {
  const { formatMessage } = useIntl();
  const removeCohort = useAnalyticalDatasetDefinitionStore((s) => s.removeCohort);
  const { cohortProfileId, selectedCohort, isCohortListLoading } = useSelectedSourceCohort();
  // Same inherited study period the Study period section reads — surfaced on
  // the row as the "date range • subjects" meta line. Its `isStale` reflects
  // the *cohort version's own* staleness (an AV it depends on changed),
  // not the analytical dataset's — that's a separate, AD-level concept.
  // TODO: cleanup with av_edit_while_in_use ff
  const { startDate, endDate, isStale } = useInheritedStudyPeriod();
  const { isEditDisabled, isProcessing } = useAnalyticalDatasetEditLock();
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const rows = useMemo<SourceCohortDisplay[]>(() => {
    if (!selectedCohort) return [];
    return [
      {
        cohortProfileId: selectedCohort.cohortProfileId,
        cohortName: selectedCohort.cohortName,
        status: selectedCohort.latestStatus,
        studyPeriodStart: startDate,
        studyPeriodEnd: endDate,
        subjectCount: selectedCohort.latestSubjectCount,
        isStale,
      },
    ];
  }, [selectedCohort, startDate, endDate, isStale]);

  const onAddCohort = useCallback(() => setIsPickerOpen(true), []);
  const onClosePicker = useCallback(() => setIsPickerOpen(false), []);
  // Single Zustand action: dropping the cohort empties this section and, since
  // the study period is inherited from the cohort, empties that section too.
  const onRemoveCohort = useCallback((id: string) => removeCohort(id), [removeCohort]);

  const hasCohort = cohortProfileId !== null;
  // Always "Add cohort" — no "Replace cohort" variant, even with one attached.
  const addLabel = formatMessage({ id: 'analyticalDatasetDetail.sourceCohorts.addButton' });

  // True when the store has a cohort attached but the project's cohort list
  // hasn't finished loading yet — without this, `rows` is empty (join fails),
  // and the section silently renders the empty state despite count=1.
  const isLoading = hasCohort && isCohortListLoading;

  const editLockTooltip = isEditDisabled
    ? formatMessage({
        id: isProcessing
          ? 'analyticalDatasetDetail.editLock.processing'
          : 'analyticalDatasetDetail.editLock.notOwner',
      })
    : undefined;

  return {
    sectionLabel: formatMessage({ id: 'analyticalDatasetDetail.sourceCohorts.label' }),
    count: hasCohort ? 1 : 0,
    addLabel,
    emptyTitle: formatMessage({ id: 'analyticalDatasetDetail.sourceCohorts.emptyTitle' }),
    emptyBody: formatMessage({ id: 'analyticalDatasetDetail.sourceCohorts.emptyBody' }),
    removeLabel: formatMessage({ id: 'analyticalDatasetDetail.sourceCohorts.removeButton' }),
    staleCohortTooltip: formatMessage({ id: 'analyticalDatasetDetail.sourceCohorts.staleWarning' }),
    rows,
    isPickerOpen,
    isLoading,
    onAddCohort,
    onClosePicker,
    onRemoveCohort,
    isEditDisabled,
    editLockTooltip,
  };
};
